<template>
    <PendingScreen v-if="!question || !ready" fullscreen />
    <component
        :is="viewController"
        v-else-if="question"
        :key="key"
        :field="field"
        :question="question"
    />
</template>

<script lang="ts">
    import noop from 'lodash/noop';
    import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
    import type { RouteLocationRaw } from 'vue-router';

    import Question from '@evidentid/ido-lib/interfaces/Question';
    import { isQuestionComplete } from '@evidentid/ido-lib/questionUtils';
    import slugify from '@evidentid/universal-framework/slugify';
    import unslugify from '@evidentid/universal-framework/unslugify';

    import PendingScreen from '@/components/screens/PendingScreen.vue';
    import Field from '@/fields/Field';
    import getField from '@/fields/getField';
    import { isGlsSession } from '@/interfaces/SessionData';
    import { useIdoStore } from '@/store/store';
    import getTransitionRoute from '@/utils/getTransitionRoute';
    import getTransitionState from '@/utils/getTransitionState';

    @Component({
        components: { PendingScreen },
    })
    export default class AnswerQuestion extends Vue {
        @Prop({ type: Function, default: getField })
        private getField!: typeof getField;

        private ready: boolean = false;

        private get id() {
            // vue-router 4 types repeated route params as `string | string[]`; this
            // route never repeats `slug` -- `as string` matches this repo's
            // established vue-router 4 param-typing fallout pattern (batch 1, ed2202df8).
            const slug = this.$route.params.slug as string;
            return slug ? unslugify(slug) : null;
        }

        private get key() {
            return this.question?.attrType || '<unknown>';
        }

        private get question() {
            const id = this.id;
            return useIdoStore().questions.find((question) => question.attrType === id);
        }

        private get nextQuestion() {
            const currentQuestion = this.question;
            const nextQuestions = useIdoStore().questions.filter(
                (question) => !isQuestionComplete(question),
            );
            const nextAttrTypes = nextQuestions.map(({ attrType }) => attrType);
            const expectedAttrTypes = currentQuestion?.metadata?.nextAttrTypes || [];
            const nextAttrType = expectedAttrTypes.find((attrType) => nextAttrTypes.includes(attrType));
            return nextQuestions.find(({ attrType }) => attrType === nextAttrType);
        }

        private get field() {
            const question = this.question;
            return question ? this.getField(question.type) : null;
        }

        private get viewController() {
            const field = this.field;
            return field ? field.viewController : null;
        }

        private get session() {
            return useIdoStore().session;
        }

        private initialize(field: Field | null, question: Question | null) {
            if (!field || !question) {
                return;
            }

            this.ready = field.ready;
            if (!this.ready) {
                this.ready = false;
                return useIdoStore().fetchAttributeData([ question.attrType ]).then(() => {
                    if (this.field === field) {
                        this.ready = true;
                        this.$forceUpdate();
                    }
                });
            }
        }

        private beforeUpdate() {
            this.safeCheck();
        }

        private created() {
            this.safeCheck();
        }

        @Watch('question')
        private onQuestionChange(question: Question | null) {
            this.initialize(question && this.getField(question.type), question);
        }

        private safeCheck() {
            const question = this.question;
            if (!question || isQuestionComplete(question) || !this.session ||
                isGlsSession(this.session)) {
                this.redirect();
            } else if (this.field) {
                this.initialize(this.field, question);
            } else {
                throw new Error(`Couldn't find a field associated with "${question.attrType}".`);
            }
        }

        private redirect() {
            const nextQuestion = this.nextQuestion;
            if (nextQuestion) {
                // PRODUCT-26110 Phase 2B2 Task 5 batch 4: pre-existing FIXME (predates
                // this migration) -- spreading the full current route (which carries
                // vue-router 4's own `path`) alongside a new `params` object is a
                // combination vue-router 4's own `RouteLocationRaw` union now flags at
                // the type level (`RouteLocationAsPathGeneric` -- the variant TS infers
                // once `path` is present -- has no `params` field at all, since path-
                // based navigation never interpolates params into the path string; only
                // the `name` + `params` variant does). That's a real behavioral
                // question the original FIXME already suspected, not something this
                // migration introduced -- preserving the exact pre-existing runtime
                // shape here rather than guessing at the intended fix (matches this
                // repo's established convention for pre-existing suspect call sites,
                // e.g. batch 1's DdrAieInput.vue), just moving the suppression from a
                // now-ineffective `@ts-ignore` (it only covered the next line, not the
                // `params` key several lines down) to a type-level cast.
                this.$router.push({
                    // PRODUCT-26110 Phase 2B2: router.currentRoute is a Ref under
                    // vue-router 4.
                    ...this.$router.currentRoute.value,
                    params: {
                        ...this.$router.currentRoute.value.params,
                        slug: slugify(nextQuestion.attrType),
                    },
                } as RouteLocationRaw);
            } else {
                this.$router.push(getTransitionRoute(getTransitionState(useIdoStore()))).catch(noop);
            }
        }
    }
</script>
